import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGGContext } from 'src/components/providers/googleAds';
import { useGoogleGrade } from 'src/hooks/google/useGoogleGrade';
import { IGoogleGrade } from 'src/types/google/GoogleGrade';
import { IAccount } from 'src/types/common/Account';
import { ConditionalClearTimeout, DelayNavigationToTimedOutError, TimeIDRef } from 'src/utils/helpers';
import { ServiceType } from 'src/enums/ServiceType';
import useGATracking from 'src/hooks/google/useGATracking';

const dataLayer = {
  event: 'gaTriggerEvent',
  gaEventCategory: 'Grader',
  gaEventAction: 'Impression',
  gaEventLabel: 'GraderStepReport',
};

interface SetAccountParams {
  grade: IGoogleGrade;
  setter: Dispatch<SetStateAction<IAccount>>;
}

const setAccount = (params: SetAccountParams): void => {
  let displayName;
  const accountId = params.grade.overallScore.accountId;
  displayName = params.grade.overallScore.accountId;
  if (params.grade.overallScore.accountName) {
    displayName = params.grade.overallScore.accountName + ' (' + params.grade.overallScore.accountId + ')';
  }
  params.setter({ accountId, accountName: displayName });
};

interface SetZeroDollarParams {
  grade: IGoogleGrade;
  setter: Dispatch<SetStateAction<boolean>>;
}

const setZeroDollar = (params: SetZeroDollarParams): void => {
  params.setter(params.grade.searchPerformanceMetrics?.spend === 0);
};

type ReturnType = {
  isZeroDollarsSpent: boolean;
};

export const useReport = (): ReturnType => {
  const context = useGGContext();
  const { UUID } = useParams();
  const navigate = useNavigate();
  const { grade, error } = useGoogleGrade(UUID);
  const [isZeroDollarsSpent, setIsZeroDollarSpent] = useState<boolean>(false);
  const timeIDRef: TimeIDRef = {
    timeID: undefined,
  };
  const { pushCustomGAEvent } = useGATracking({
    pageViewDefaults: { title: 'GoogleAdsGrader Report' },
    triggerOnMount: true,
  });

  useEffect(() => {
    if (UUID === undefined) navigate('/');
  }, []);

  useEffect(() => {
    if (!grade && !error) {
      timeIDRef.timeID = DelayNavigationToTimedOutError(navigate, ServiceType.GOOGLE);
    } else if (grade && !error) {
      ConditionalClearTimeout(timeIDRef);
      // Currently we are pushing event only with Search Report
      pushCustomGAEvent(dataLayer);
      setAccount({ grade, setter: context.setGoogleAdsAccount });
      setZeroDollar({ grade, setter: setIsZeroDollarSpent });
    } else if (error) {
      ConditionalClearTimeout(timeIDRef);
      navigate('/google-grader/error?event=noRoute');
    }

    return () => ConditionalClearTimeout(timeIDRef);
  }, [grade, error]);

  return { isZeroDollarsSpent };
};
